import React from 'react';
import { 
  MagnifyingGlassIcon, 
  ChevronRightIcon,
  LightBulbIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  PresentationChartLineIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import PlaybookCallout from '../../../components/playbooks/PlaybookCallout';

const EmployerBrandStage1 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 1: Discover Your True EVP" icon={<MagnifyingGlassIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* Introduction */}
      <PlaybookCard>
        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300 mb-2">
            The first step on this playbook is all about discovering & understanding your EVP. This is absolutely crucial to do before you start thinking about things like employer brand. You need to know:
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">Why do people decide to join & stay with you?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">Why do people thrive & fail?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">What value do we & don't we offer?</span>
            </li>
          </ul>
          
        </div>
      </PlaybookCard>

      {/* Watch This Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <SparklesIcon className="h-7 w-7 text-primary-light mr-2" />
          Watch This
        </h3>
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
          <iframe
            src="https://www.loom.com/embed/2435b4c3cc4e41ec88bc1381c2fdd522?sid=ae3fb7ef-032b-45da-973f-927a4f9cc86e"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          />
        </div>
      </PlaybookCard>

      {/* What You Need Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <LightBulbIcon className="h-7 w-7 text-primary-light mr-2" />
          What You Might Need
        </h3>
        <ul className="space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">Surveying Tool (e.g. Typeform or similar)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">Handful of employees to interview 1:1 (~5-10% of headcount)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">A way to synthesize & theme your data</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">Company Values or Operating Principles defined & in place</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">Internal Buy-in from Exec Team</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-300">Appropriate Tooling (Miro, Typeform & Slack)</span>
          </li>
        </ul>
      </PlaybookCard>

      {/* Discovery & Data Gathering */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <MagnifyingGlassIcon className="h-7 w-7 text-primary-light mr-2" />
          Discovery & Data Gathering
        </h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold text-white mb-2">What's involved</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Org Wide Survey</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">1:1 Interviews</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-2">Goal</h4>
            <p className="text-gray-300">
              Gather as much data & feedback as you can about why folks love working here, but also what they'd improve too.
            </p>
          </div>

          {/* Step 1: Comms */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-2" />
              Step 1: Comms
            </h4>
            <p className="text-gray-300 mb-4">
              We'd suggest kicking this off with some pre-comms that tee's the survey up nicely, and get's folks warmed up to when & why this is happening, and who's involved and what you need from them.
            </p>
            <CopyToNotion title="Example Slack Message" slack={true}>
              <div className="space-y-4">
                <p className="text-gray-300 font-medium">TL;DR - We want to know what makes [company] attractive to you, and what we could improve so we can attract & repel the right people when we hire and improving your experience here too. We'd love your help 🙏</p>
                <p className="text-gray-300">Hey Folks,</p>
                <p className="text-gray-300">We want to do some discovery work around our EVP (Employee Value Proposition, a.k.a. where you do & don't get value from working here) and make sure we're building an employer brand that is as open & authentic as possible, so we can attract & retain the right people, and help others opt out.</p>
                <p className="text-gray-300">We'd love your help on two things… 🙏</p>
                <ol className="list-decimal pl-6 space-y-2 text-gray-300">
                  <li>Quick Survey- On [Date] we're going to share a short survey (~10 mins) just asking some questions about working here (best bits, improvements etc). This will be open to everyone for [x hours/days] and we'd appreciate as much candid feedback as you can give.</li>
                  <li>EVP Interviews- We'd love to spend 30 mins with a few of you to dive deeper 1:1 and just ask you a few Qs about your experience here. For clarity we'll select 5-10% of the total team to make sure we have a diverse cross-section of the business, but if you'd like to either volunteer, or opt-out please DM after reading this.</li>
                </ol>
              </div>
            </CopyToNotion>
          </div>

          {/* Step 2: Survey */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
              <ClipboardDocumentListIcon className="h-5 w-5 text-primary-light mr-2" />
              Step 2: Survey
            </h4>
            <p className="text-gray-300 mb-4">
              Reminder, this should be org-wide and probably between 20-30 questions at most. You want a nice balance between quantitative & qualitative data.
            </p>
            <ToolCallout
              tool="typeform"
              label="Example Survey Template"
              href="https://form.typeform.com/to/nolo2BUJ"
              description="A comprehensive survey template you can copy and customize"
            />
          </div>

          {/* Step 3: Interviews */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
              <UserGroupIcon className="h-5 w-5 text-primary-light mr-2" />
              Step 3: Interviews
            </h4>
            <p className="text-gray-300 mb-4">
              Parallel to the survey, spending some time in some deeper dive 1:1's with a cross-section of your team is really helpful.
            </p>
            <CopyToNotion title="Example Interview Questions">
              <div className="space-y-4">
                <p className="text-gray-300">If your friend wanted to join this company and you told them the best thing about working here, what would it be?</p>
                <p className="text-gray-300">What things should they also be aware of before they join? (e.g. trade-offs)</p>
                <p className="text-gray-300">What's your hardest day been here, and why?</p>
                <p className="text-gray-300">We hand you a Magic Wand. What do you change and why?</p>
              </div>
            </CopyToNotion>
          </div>

          {/* Step 4: Synthesize */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
              <PresentationChartLineIcon className="h-5 w-5 text-primary-light mr-2" />
              Step 4: Synthesize
            </h4>
            <p className="text-gray-300 mb-4">
              Work with the data gathered to distill down, theme and find patterns.
            </p>
            <div className="space-y-4">
              <h5 className="text-md font-semibold text-white">Goals</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Find the top 3-5 things that people genuinely value about working in this business</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Identify clear signal on the areas that could be improved across PX</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PlaybookCard>

      {/* Need Help Section */}
     
    </PlaybookSection>
  </div>
);

export default EmployerBrandStage1; 